import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Building2, Check, Copy, Loader2, QrCode, Star, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

function CopyRow({ label, value, copied, onCopy }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-3">
        <p className="min-w-0 break-all text-base font-bold text-gray-900">{value}</p>
        <button type="button" onClick={() => onCopy(value, label)} className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm hover:text-brand-primary" aria-label={`Copiar ${label}`}>
          {copied === label ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export function QRModal({ open, onClose }) {
  const [methods, setMethods] = useState([]);
  const [activeTab, setActiveTab] = useState('qr');
  const [selectedIds, setSelectedIds] = useState({ qr: null, bank: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();

    const loadMethods = async () => {
      setLoading(true); setError(''); setCopied('');
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/driver/payment-methods`, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const rows = response.data.methods || [];
        const qrCodes = rows.filter((method) => method.method_type === 'qr');
        const accounts = rows.filter((method) => method.method_type === 'bank');
        const primaryQr = qrCodes.find((method) => method.is_primary) || qrCodes[0] || null;
        const primaryBank = accounts.find((method) => method.is_primary) || accounts[0] || null;
        setMethods(rows);
        setSelectedIds({ qr: primaryQr?.id || null, bank: primaryBank?.id || null });
        setActiveTab(primaryQr ? 'qr' : 'bank');
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') setError(err.response?.data?.error || 'No se pudieron cargar los metodos de pago.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadMethods();
    return () => controller.abort();
  }, [open]);

  const qrCodes = useMemo(() => methods.filter((method) => method.method_type === 'qr'), [methods]);
  const bankAccounts = useMemo(() => methods.filter((method) => method.method_type === 'bank'), [methods]);
  const currentOptions = activeTab === 'qr' ? qrCodes : bankAccounts;
  const selected = currentOptions.find((method) => method.id === selectedIds[activeTab]) || currentOptions[0];

  if (!open) return null;

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(''), 1600);
    } catch (copyError) {
      setError('No se pudo copiar automaticamente. Mantenga presionado el dato para copiarlo.');
    }
  };

  const selectMethod = (method) => {
    setSelectedIds((current) => ({ ...current, [activeTab]: method.id }));
    const token = localStorage.getItem('token');
    axios.post(
      `${API_URL}/api/driver/payment-methods/${method.id}/selection`,
      {},
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    ).catch(() => {
      // Telemetry must not prevent the driver from displaying an active method.
    });
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4">
      <button className="absolute inset-0 bg-black/55" onClick={onClose} aria-label="Cerrar" />
      <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div><h2 className="text-xl font-black text-gray-900">Metodos de pago Fast Max</h2><p className="mt-0.5 text-xs text-gray-500">Muestra al cliente una opcion activa.</p></div>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100" aria-label="Cerrar modal"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex border-b border-gray-200 px-4">
          <button type="button" onClick={() => setActiveTab('qr')} disabled={!qrCodes.length && !loading} className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm font-bold disabled:opacity-40 ${activeTab === 'qr' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500'}`}><QrCode className="h-4 w-4" /> QR</button>
          <button type="button" onClick={() => setActiveTab('bank')} disabled={!bankAccounts.length && !loading} className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm font-bold disabled:opacity-40 ${activeTab === 'bank' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500'}`}><Building2 className="h-4 w-4" /> Transferencia</button>
        </div>
        <div className="overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center py-14 text-gray-500"><Loader2 className="mb-3 h-8 w-8 animate-spin text-brand-primary" />Cargando metodos...</div>
          ) : error && !methods.length ? (
            <div className="rounded-xl bg-red-50 p-5 text-center text-sm font-semibold text-red-700">{error}</div>
          ) : !selected ? (
            <div className="rounded-xl border border-dashed border-gray-300 px-5 py-12 text-center text-gray-500">
              {activeTab === 'qr' ? <QrCode className="mx-auto mb-3 h-10 w-10" /> : <Building2 className="mx-auto mb-3 h-10 w-10" />}
              <p className="font-bold">No hay {activeTab === 'qr' ? 'codigos QR' : 'cuentas bancarias'} activos.</p><p className="mt-1 text-sm">Solicita a administracion configurar una opcion.</p>
            </div>
          ) : (
            <>
              {currentOptions.length > 1 && (
                <div className="mb-4">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">{activeTab === 'qr' ? 'Cambiar QR' : 'Cambiar cuenta'}</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {currentOptions.map((method) => (
                      <button key={method.id} type="button" onClick={() => selectMethod(method)} className={`flex-none rounded-lg border px-3 py-2 text-left text-sm font-bold ${selected.id === method.id ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-gray-200 text-gray-600'}`}>
                        <span className="flex items-center gap-1">{method.is_primary && <Star className="h-3.5 w-3.5 fill-current text-amber-500" />}{method.label || (activeTab === 'qr' ? method.phone_number : method.bank_name)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === 'qr' ? (
                <div className="text-center">
                  {selected.is_primary && <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800"><Star className="h-3.5 w-3.5 fill-current" /> QR principal</span>}
                  <img src={selected.qr_image_url} alt={`QR de ${selected.holder_name}`} className="mx-auto max-h-[45vh] w-full max-w-sm rounded-xl border border-gray-200 object-contain" />
                  <h3 className="mt-4 text-lg font-black text-gray-900">{selected.holder_name}</h3>{selected.label && <p className="text-sm text-gray-500">{selected.label}</p>}
                  <div className="mx-auto mt-4 max-w-sm"><CopyRow label="Celular" value={selected.phone_number} copied={copied} onCopy={copyValue} /></div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 rounded-xl bg-blue-50 p-4">
                    <div className="flex items-center justify-between gap-2"><div><p className="text-xs font-bold uppercase tracking-wide text-blue-600">Banco</p><h3 className="text-xl font-black text-blue-950">{selected.bank_name}</h3></div><span className="rounded-full bg-white px-3 py-1 text-sm font-black text-blue-800">{selected.currency}</span></div>
                    <p className="mt-2 text-sm font-semibold text-blue-900">Titular: {selected.holder_name}</p>{selected.is_primary && <p className="mt-1 flex items-center gap-1 text-xs font-bold text-amber-700"><Star className="h-3.5 w-3.5 fill-current" /> Cuenta principal</p>}
                  </div>
                  <div className="space-y-3"><CopyRow label="Numero de cuenta" value={selected.account_number} copied={copied} onCopy={copyValue} /><CopyRow label="CCI" value={selected.cci} copied={copied} onCopy={copyValue} /></div>
                </div>
              )}
              {error && <p className="mt-3 text-center text-xs font-semibold text-red-600">{error}</p>}
              <p className="mt-5 text-center text-xs text-gray-400">La visualizacion de estos datos no confirma el pago.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
